import * as React from 'react'
import { ScrollView, Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Button from '@components/button/Button'
import Header from '@components/header/Header'
import TenantHeader from './TenantHeader'
import TenantTopSection from './TenantTopSection'
import IncidentListSection from '@components/sections/IncidentListSection'

import api from '@api/api'
import logger from '@utils/logger'

import * as SiteStore from '@store/site/site'
import * as LocationStore from '@store/location/location'
import * as FiltreStore from '@store/filtre/filtre'
import { emptyFilters } from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'
import { findLocalisationParents } from '@screens/incident/utils'

const MAIN_VERTICAL_PADDING = 24

interface Props {
  tenantId: string
}

const TenantDetailScreen = ({ tenantId }: Props) => {
  const i18n = useI18n()
  const site = useReducer(SiteStore.store, (s) => s.site!)

  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const places = useReducer(LocationStore.store, (s) => s.places)

  const { tenant, dates } = tenants.current.find((t) => t.tenant.id === tenantId) || {
    tenant: undefined,
    dates: undefined,
  }

  const navigation = useNavigation()

  const [incidentStatus, setIncidentStatus] = React.useState<Status>('fetched')
  const [hasScrolled, setHasScrolled] = React.useState(false)
  const [incidents, setIncidents] = React.useState<IncidentResponse[] | undefined>(undefined)
  const [edlList, setEdlList] = React.useState<EtatDesLieuxResponse[]>([])

  const [{ height, width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const canStartIncident = !!dates && dates.status === 'current'

  React.useEffect(() => {
    api.edl.all(site.id, undefined, tenantId).then(setEdlList).catch(logger.error)
  }, [site])

  React.useEffect(() => {
    if (!!tenant && incidentStatus !== 'loading' && !incidents) {
      setIncidentStatus('loading')
      Promise.all(tenant.locaux.map(({ localisationId }) => api.incident.search({ localisationIds: [localisationId] })))
        .then((incs) => {
          const allIncidents = incs.reduce((acc, cur) => [...acc, ...cur], [] as IncidentResponse[])
          setIncidents(allIncidents)
          setIncidentStatus('fetched')
        })
        .catch((err) => {
          logger.error(err)
          setIncidentStatus('error')
        })
    }
  }, [tenant])

  const onMapPressed = () => navigation.push('/plan', { externalIncident: incidents || [] })

  const startButton: HeaderButton = {
    label: i18n.t('screens.site.startIncident'),
    onClick: () => navigation.push('/incident/start'),
  }

  const mapButton: HeaderButton = {
    label: i18n.t('screens.tenant.seeMap'),
    onClick: onMapPressed,
    color: 'light',
  }

  const headerButtons: HeaderButton[] = React.useMemo(() => {
    if (!tenant) {
      return []
    }
    const buttons = [mapButton]

    if (canStartIncident) {
      buttons.push(startButton)
    }
    return buttons
  }, [tenant, i18n, canStartIncident])

  const headerLinks = [
    { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
    { label: i18n.t('screens.tenants.title'), onClick: () => navigation.push('/tenants') },
  ]

  const renderBottomButton =
    isPhone && dates && dates.status === 'current'
      ? () => (
          <BottomButton width={width}>
            <Button status="active" label={startButton.label} onClick={startButton.onClick} />
          </BottomButton>
        )
      : undefined

  const renderBottomHeader = () =>
    !!dates &&
    dates.start &&
    dates.end && (
      <TenantDate>
        {i18n.t(`screens.tenants.${dates.status}Date`, {
          date: new Date(dates.status === 'former' ? dates.end : dates.start),
        })}
      </TenantDate>
    )

  //TODO: not implemented
  const displayDocuments = true

  const onSeeAll = () => {
    const location = !!tenant
      ? tenant.locaux.reduce((acc, cur) => {
          const curPlace = places.find((pl) => pl.id === cur.localisationId)

          if (!!curPlace && curPlace.enabled) {
            const curSpace = findLocalisationParents(places, curPlace).find((sp) => !sp.parentId)

            if (!!curSpace && !acc.find((id) => id === curSpace.id)) {
              acc.push(curSpace.id)
            }
          }
          return acc
        }, [] as string[])
      : []

    FiltreStore.actions.setIncidentFilter({ ...emptyFilters, location })

    navigation.push('/incidents')
  }

  return (
    <Root
      loading={incidentStatus === 'loading'}
      renderFixedBottom={renderBottomButton}
      header={i18n.t('screens.tenant.title')}
      renderHeader={
        isPhone && !!tenant
          ? () => (
              <TenantHeader
                edlList={edlList}
                hasScrolled={hasScrolled}
                tenant={tenant}
                onBack={navigation.goBack}
                displayDocuments={displayDocuments}
                onMapPressed={onMapPressed}
              />
            )
          : undefined
      }
      fixed
      onLeft={navigation.goBack}>
      <ScrollView
        scrollIndicatorInsets={{ right: 1 }}
        bounces={false}
        keyboardShouldPersistTaps="handled"
        onScroll={(event) => setHasScrolled(event.nativeEvent.contentOffset.y > 0)}
        scrollEventThrottle={16}>
        {!!tenant ? (
          <>
            {!isPhone && (
              <Header
                links={headerLinks}
                title={tenant.nomLocatairePermanent}
                buttons={headerButtons}
                renderBottom={renderBottomHeader}
              />
            )}
            <MainContainer height={height} topPadding={displayDocuments}>
              {!isPhone && <TenantTopSection tenant={tenant} edlList={edlList} />}
              <IncidentListSection incidents={incidents || []} onSeeAll={onSeeAll} />
            </MainContainer>
          </>
        ) : (
          <MainContainer height={height}>
            <MissingInfo>{i18n.t('common.error')}</MissingInfo>
          </MainContainer>
        )}
      </ScrollView>
    </Root>
  )
}

export default TenantDetailScreen

const MainContainer = styled(View)<{ height: number; topPadding?: boolean }>`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
  ${(props) => props.theme.breakpoint === 'phone' && `padding-bottom: ${props.height / 2}px;`}
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
const BottomButton = styled(View)<{ width: number }>`
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background}E6;
  padding: 16px;
`
const TenantDate = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-top: 8px;
`
